import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import { getCard, getGap } from '@Components/BigBrowse/Model/Classes/GridCard';
import { PlaneTypes } from '@Components/BigBrowse/Model/ModelTypes';
import type { BrandConcessionContentDM } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/MultiSectionView.types';
import { get2ColumnsWidth } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/columnCalculator';
import type { ViewData } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/multisection.data';
import { getMultiSectionFigmaLayout } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/sections.common';
import { JUMP_LINE_GAP } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/footer/helpers/footer.figma';
import { isOdd } from '@Utils/miscUtils';

export const doubleIndexChecker =
  (checker: (index: number) => boolean) => (index: number) =>
    checker(Math.floor(index / 2));

export async function layoutJumps(viewData: ViewData): Promise<ViewData> {
  const cards = viewData.modelData.jumps?.map(getJumpCard(viewData));

  const layoutTable = distributeItemsInColumnsWithEvenIndex({
    items: cards ?? [],
    columns: viewData.layoutTable!,
    gapAdder: getJumpGap(viewData),
    lineGapAdder: getLineGap(viewData),
  });

  return { ...viewData, layoutTable };
}

const getJumpCard =
  (viewData: ViewData) =>
  (
    jump: BrandConcessionContentDM['jumps'][number]
  ): GridCard<PlaneTypes.Card_MultiSection_Footer_Item> => {
    const side = get2ColumnsWidth(viewData);
    const layout = getMultiSectionFigmaLayout(viewData);

    const card = getCard<PlaneTypes.Card_MultiSection_Footer_Item>({
      type: PlaneTypes.Card_MultiSection_Footer_Item,
      size: { width: side, height: side },
      data: {
        type: undefined,
        id: jump.concessionId.toString(),
        key: [
          'brandConcession',
          viewData.modelData.concessionId,
          'jump',
          jump.concessionId,
        ].join('_'),
        title: jump.title,
        imageUrl: jump.imageUrl,
        destination: {
          journey: 'brandconcession',
          journeyParam: jump.concessionId,
        },
        jumpDto: jump,
        layout,
      },
    });

    // @ts-expect-error
    card.key = card.data.key;

    return card;
  };
const getJumpGap = (viewData: ViewData) => (): GridCard<PlaneTypes> => {
  const side = get2ColumnsWidth(viewData);
  return getGap({ width: viewData.columnWidth!, height: side });
};
const getLineGap = (viewData: ViewData) => (): GridCard<PlaneTypes> => {
  const height =
    getMultiSectionFigmaLayout(viewData) === 'full'
      ? JUMP_LINE_GAP
      : JUMP_LINE_GAP / 2;

  return getGap({ width: viewData.columnWidth!, height });
};

type DistributeItemsInColumnsWithEvenIndexParams<T> = {
  items: T[];
  columns: T[][];
  gapAdder: () => T;
  lineGapAdder: () => T;
};

function distributeItemsInColumnsWithEvenIndex<T>({
  items,
  columns,
  gapAdder,
  lineGapAdder,
}: DistributeItemsInColumnsWithEvenIndexParams<T>): T[][] {
  const itemsToDistribute = items.slice();
  const columnsToDistribute = columns.slice();

  while (itemsToDistribute.length) {
    columnsToDistribute.forEach((column, columnIndex) => {
      if (isOdd(columnIndex)) {
        return column.push(gapAdder(), lineGapAdder());
      }

      const item = itemsToDistribute.shift();
      return column.push(item ?? gapAdder(), lineGapAdder());
    });
  }

  return columnsToDistribute;
}
